import { useState } from "react";

import styled from "styled-components/macro";

import API from "Signups/API";
import { COST_TYPES } from "Signups/SharedComponents/costTypes";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useSignups } from "Signups/state";
import { AnimatePresence, motion } from "framer-motion";

import FnrCheckbox from "UI/FnrEnrichedCheckbox/FnrCheckbox";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import { useRefreshSignupListsData } from "../../useRefreshSignupListsData";

import CostTypeAndAmount from "./CostTypeAndAmount/CostTypeAndAmount";
import SignupFundSetting from "./SignupFundSetting";
import SignupsFundItSplash from "./SignupsFundItSplash";
import SuggestedDonationCheckbox from "./SuggestedDonationCheckbox";
import { useSetInitialPricingValues } from "./useSetInitialPricingValues";

export default function FundIt() {
  const [
    {
      hasFundIt,
      currentSignupList: {
        fundIt: { fund },
        ID,
      },
      funds,
      canManageSignupPayments,
    },
    dispatch,
  ] = useSignups();

  useSetInitialPricingValues();
  const refreshSignupListsData = useRefreshSignupListsData();

  const { isArchived } = useIsSignupClosed();

  const [isSaving, setIsSaving] = useState(false);

  const isMounted = useComponentIsMounted();

  const hasFund = fund?.ID > 0;
  const hasSignupPaymentsCollected = Boolean(fund?.signupPaymentsCollected);

  const toggleFundIt = () => {
    setIsSaving(true);

    const isTurningOn = !hasFund;

    const postData = {
      signupListID: ID,
      hasSignupCost: isTurningOn,
      ...(isTurningOn
        ? { isSuggestedDonation: false, costType: COST_TYPES.PER_SLOT }
        : {}),
    };

    API.editFundItOptions(ID, postData)
      .post()
      .then(({ fund }) => {
        let fundTransformedForList;
        let fundTransformedForSignup;
        if (isTurningOn) {
          if (!fund) {
            console.info(
              `Whoops, we should be getting back the fund if we successfully turned on FundIt (instead we got back: ${fund})`
            );
            return;
          }

          // we need to match the shape from the other calls
          fundTransformedForList = {
            ...fund,
            fundID: fund.ID,
            fundName: fund.description,
            fundDetails: fund.details,
          };

          fundTransformedForSignup = {
            ...fund,
            name: fund.description,
          };
        }

        // Common actions
        dispatch({
          type: "setSignupFund",
          payload: isTurningOn ? fundTransformedForSignup : null,
        });
        dispatch({
          type: "setSignupListCostsMoney",
          payload: isTurningOn,
        });
        dispatch({
          type: "updateAllSignupListItems",
          key: "hasSignupCost",
          value: isTurningOn,
        });
        dispatch({
          type: "Update Edit Signup Info",
          key: "hasDisabledFundItViaItem",
          value: false,
        });

        if (isTurningOn) {
          dispatch({
            type: "setSignupListIsPerSignupCostType",
            payload: false,
          });
          dispatch({
            type: "updateAllSignupListItems",
            key: "costType",
            value: COST_TYPES.PER_SLOT,
          });
          dispatch({
            type: "setFundsList",
            payload: [...funds, fundTransformedForList],
          });

          return;
        }

        refreshSignupListsData();

        dispatch({
          type: "setIsSuggestedDonation",
          payload: false,
        });
        // TODO warn Betty that all the prices and options set on items will be lost (if applicable)
        dispatch({
          type: "updateAllSignupListItems",
          key: "amount",
          value: null,
        });
      })
      .catch((err) => {
        alert(
          "Oh no! Something went wrong. Please refresh and try again, or contact help@flocknote.com."
        );
        console.log("err:", err);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // TODO pull up common styles between here and SignupOption/SignupSubOption
  const hidden = {
    opacity: 0,
    height: 0,
    y: -20,
  };

  if (!hasFundIt) {
    return (
      <Wrapper data-cy="signupFundItWrapper">
        <SignupsFundItSplash />
      </Wrapper>
    );
  }

  if (!canManageSignupPayments)
    return (
      <p>
        It looks like you don&apos;t have permission to manage signup payments.
        Please contact your admin to obtain this permission.
      </p>
    );

  return (
    <Wrapper data-cy="signupFundItWrapper">
      <FnrCheckbox
        on={hasFund}
        toggleOn={toggleFundIt}
        disabled={hasSignupPaymentsCollected || isArchived}
        loading={isSaving}
        data-cy="fnrCheckbox"
      >
        Take payments with this Signup
      </FnrCheckbox>

      <AnimatePresence>
        {hasFund && (
          <FundItOptions
            initial={isMounted ? hidden : null}
            animate={{
              opacity: 1,
              height: "auto",
              y: 0,
            }}
            exit={{
              ...hidden,
              transition: {
                type: "tween",
                duration: 0.3,
                ease: "backIn",
              },
            }}
          >
            <SignupFundSetting />
            {/* Force a new component instance when we archive in order to reset the state */}
            <CostTypeAndAmount key={isArchived} />
            <SuggestedDonationCheckbox />
          </FundItOptions>
        )}
      </AnimatePresence>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 18px;
  box-sizing: border-box;
  max-height: 100%;
  overflow: auto;
  padding-bottom: 140px;
`;

const FundItOptions = styled(motion.div)`
  margin-left: 35px;

  @media screen and (max-width: 480px) {
    margin-left: 0;
  }
`;
